<template>
  <section class="hero-carousel-section">
    <div class="hero-carousel-container">
      <!-- Custom Indicators -->
      <div class="hero-indicators">
        <button
          v-for="(slide, index) in heroSlides"
          :key="index"
          type="button"
          @click="setActiveSlide(index)"
          :class="{ active: index === currentSlide }"
          :aria-label="`Slide ${index + 1}`"
        ></button>
      </div>

      <!-- Custom Carousel Items -->
      <div class="hero-slides-container">
        <div
          v-for="(slide, index) in heroSlides"
          :key="index"
          :class="['hero-slide', { active: index === currentSlide }]"
        >
          <div class="hero-background" :style="{ backgroundImage: `url(${slide.image})` }"></div>
          <div class="hero-overlay"></div>

          <div class="hero-full-container">
            <div class="hero-full-grid">
              <!-- Left Side: Content -->
              <div class="hero-content-section">
                <div class="hero-content-left animate-fade-in">
                  <div class="hero-badge mb-3">
                    <span class="badge bg-gradient-primary-full">
                      {{ slide.badge }}
                    </span>
                  </div>

                  <h1 class="hero-title mb-4 text-gradient-primary">
                    <span ref="heroTitleRef" class="typewriter-text" :class="{ typing: isTyping }">{{ displayedTitle }}</span>
                  </h1>

                  <p class="hero-subtitle mb-4">
                    {{ slide.subtitle }}
                  </p>

                  <div class="hero-details mb-4">
                    <div class="detail-item mb-3">
                      <SvgIcon name="calendar" size="lg" color="white" class="me-3" />
                      <div class="detail-content">
                        <div class="detail-label">Date</div>
                        <div class="detail-value">{{ slide.date }}</div>
                      </div>
                    </div>
                    <div class="detail-item mb-3">
                      <SvgIcon name="building" size="lg" color="white" class="me-3" />
                      <div class="detail-content">
                        <div class="detail-label">Venue</div>
                        <div class="detail-value">{{ slide.venue }}</div>
                      </div>
                    </div>
                    <div class="detail-item mb-4">
                      <SvgIcon name="location" size="lg" color="white" class="me-3" />
                      <div class="detail-content">
                        <div class="detail-label">Location</div>
                        <div class="detail-value">{{ slide.location }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="hero-actions">
                    <button class="btn btn-gradient-primary btn-lg me-3 hover-glow" @click="scrollToRegistration">
                      <SvgIcon name="user-plus" size="md" color="white" class="me-2" />
                      Register Now
                    </button>
                    <button class="btn btn-outline-light btn-lg hover-lift" @click="scrollToAgenda">
                      <SvgIcon name="calendar" size="md" color="white" class="me-2" />
                      View Agenda
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right Side: Guest of Honor Image -->
              <div class="hero-metrics-section">
                <div class="hero-image animate-fade-in-right">
                  <img
                    src="/assets/Guest of Honor.jpeg"
                    alt="Guest of Honor"
                    class="guest-honor-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import { onMounted, ref, onUnmounted } from 'vue'
import { animate, stagger } from 'animejs'
import SvgIcon from './SvgIcon.vue'

export default {
  name: 'HeroCarousel',
  components: {
    SvgIcon
  },
  setup() {
    const currentSlide = ref(0)
    let autoPlayInterval = null

    // Typewriter animation state
    const displayedTitle = ref('')
    const isTyping = ref(false)
    let typewriterTimeout = null
    const heroSlides = [
      {
        image: require('@/assets/International Digital Workplace Summit 2025/panel-picture.jpg'),
        badge: '25th - 27th February 2026',
        title: 'International Digital Workplace Summit',
        subtitle: 'Transforming the Global Digital Economy - Join 300+ Leaders',
        date: '25th - 27th February 2026',
        venue: 'Golden Tulip Zanzibar Airport Hotel',
        location: 'Zanzibar, Tanzania'
      },
      {
        image: require('@/assets/International Digital Workplace Summit 2025/speaker.jpg'),
        badge: 'WORLD-CLASS SPEAKERS',
        title: 'Learn from Industry Leaders',
        subtitle: 'Connect with global experts and visionary leaders shaping the future of work.',
        date: '25th - 27th February 2026',
        venue: 'Golden Tulip Zanzibar Airport Hotel',
        location: 'Zanzibar, Tanzania'
      },
      {
        image: require('@/assets/International Digital Workplace Summit 2025/members.jpg'),
        badge: 'NETWORKING OPPORTUNITIES',
        title: 'Build Lasting Connections',
        subtitle: 'Network with 300+ HR Professionals, Tech Leaders and Innovation Pioneers',
        date: '25th - 27th February 2026',
        venue: 'Golden Tulip Zanzibar Airport Hotel',
        location: 'Zanzibar, Tanzania'
      },
      {
        image: require('@/assets/International Digital Workplace Summit 2025/speaker1.jpg'),
        badge: 'INNOVATION SHOWCASE',
        title: 'Discover Cutting-Edge Solutions',
        subtitle: 'Explore the Latest Technologies and Innovations Transforming Workplaces ',
        date: '25th - 27th February 2026',
        venue: 'Golden Tulip Zanzibar Airport Hotel',
        location: 'Zanzibar, Tanzania'
      }
    ]


    const scrollToRegistration = () => {
      const element = document.getElementById('registration-section')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        // Navigate to registration page if section doesn't exist
        window.location.href = '/registration'
      }
    }

    const scrollToAgenda = () => {
      const element = document.getElementById('agenda-section')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        // Navigate to agenda page if section doesn't exist
        window.location.href = '/agenda'
      }
    }

    onMounted(() => {
      // Initialize the first slide's title with typewriter effect
      typewriterEffect(heroSlides[0].title)

      // Animate left content on mount
      animate('.hero-content-left', {
        opacity: [0, 1],
        translateX: [-50, 0],
        duration: 1000,
        easing: 'easeOutExpo'
      })

      // Animate right image
      setTimeout(() => {
        animate('.hero-image', {
          opacity: [0, 1],
          translateX: [50, 0],
          duration: 800,
          easing: 'easeOutExpo'
        })

        animate('.guest-honor-image', {
          opacity: [0, 1],
          translateY: [30, 0],
          scale: [0.9, 1],
          duration: 600,
          delay: 200,
          easing: 'easeOutBack'
        })
      }, 300)

      // Set up intersection observer for scroll animations
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animate(entry.target.querySelectorAll('.animate-fade-in'), {
              opacity: [0, 1],
              translateY: [30, 0],
              duration: 600,
              delay: stagger(100),
              easing: 'easeOutExpo'
            })

            animate(entry.target.querySelectorAll('.animate-fade-in-right'), {
              opacity: [0, 1],
              translateX: [30, 0],
              duration: 600,
              delay: 200,
              easing: 'easeOutExpo'
            })
          }
        })
      })

      // Start auto play
      startAutoPlay()

      // Observe carousel items for animation
      document.querySelectorAll('.hero-slide').forEach(item => {
        observer.observe(item)
      })
    })

    // Typewriter animation function
    const typewriterEffect = (text, speed = 80) => {
      // Clear any existing typewriter animation
      if (typewriterTimeout) {
        clearTimeout(typewriterTimeout)
      }

      // Reset display state
      displayedTitle.value = ''
      isTyping.value = true

      let i = 0
      const typeCharacter = () => {
        if (i < text.length) {
          displayedTitle.value += text.charAt(i)
          i++
          typewriterTimeout = setTimeout(typeCharacter, speed)
        } else {
          isTyping.value = false
        }
      }

      // Start typing after a short delay for better visual effect
      setTimeout(typeCharacter, 300)
    }

    // Custom carousel methods
    const setActiveSlide = (index) => {
      currentSlide.value = index
      // Trigger typewriter animation for the new slide's title
      typewriterEffect(heroSlides[index].title)
      restartAutoPlay()
    }

    const nextSlide = () => {
      const newIndex = (currentSlide.value + 1) % heroSlides.length
      setActiveSlide(newIndex)
    }

    const startAutoPlay = () => {
      autoPlayInterval = setInterval(() => {
        nextSlide()
      }, 5000)
    }

    const restartAutoPlay = () => {
      clearInterval(autoPlayInterval)
      startAutoPlay()
    }

    onUnmounted(() => {
      clearInterval(autoPlayInterval)
      // Cleanup typewriter timeout
      if (typewriterTimeout) {
        clearTimeout(typewriterTimeout)
      }
    })

    return {
      heroSlides,
      scrollToRegistration,
      scrollToAgenda,
      currentSlide,
      setActiveSlide,
      displayedTitle,
      isTyping
    }
  }
}
</script>

<style scoped>
.hero-carousel-section {
  position: relative !important;
  height: 100vh !important;
  min-height: 700px !important;
  overflow: hidden !important;
  background: #000 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.hero-carousel-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.hero-slides-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.hero-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 1s ease-in-out;
  display: flex;
  align-items: center;
}

.hero-slide.active {
  opacity: 1;
}

/* Full-width hero layout */
.hero-full-container {
  height: 100%;
  width: 100%;
  max-width: 100vw;
  padding: 0 60px;
  display: flex;
  align-items: center;
}

.hero-full-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 80px;
  width: 100%;
  height: 100%;
  align-items: center;
  min-height: calc(100vh - 160px);
}

.hero-content-section {
  height: 100%;
  display: flex;
  align-items: center;
}

.hero-metrics-section {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Override Bootstrap utility classes */
.hero-slide .mb-3,
.hero-slide .mb-4,
.hero-slide .mb-2 {
  margin-bottom: var(--spacing-md) !important;
}

.hero-slide .h-100 {
  height: 100% !important;
}

.hero-slide .align-items-center {
  align-items: center !important;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--idws-gradient-hero);
  z-index: 2;
}

/* Left Side Content */
.hero-content-left {
  position: relative !important;
  z-index: 3 !important;
  color: white !important;
  padding: var(--spacing-4xl) 0 var(--spacing-xl) 0 !important;
  margin: 0 !important;
  height: auto !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
}

.hero-badge .badge {
  font-size: var(--font-size-sm);
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 600;
  border-radius: 20px;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--idws-gradient-accent);
  border: none;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: var(--spacing-lg);
  /* Reserve space for two lines to prevent layout shift */
  min-height: calc(clamp(2.5rem, 5vw, 3.5rem) * 1.2 * 2);
  display: flex;
  align-items: flex-start;
}

.hero-subtitle {
  font-size: var(--font-size-lg);
  font-weight: 400;
  line-height: 1.5;
  opacity: 0.95;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: var(--spacing-xl);
}

.hero-details {
  margin-bottom: var(--spacing-xl);
}

.detail-item {
  display: flex;
  align-items: center;
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
  margin-bottom: 2px;
}

.detail-value {
  font-size: var(--font-size-lg);
  color: white;
  font-weight: 600;
}

.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

/* Right Side Guest Image */
.hero-image {
  position: relative !important;
  z-index: 3 !important;
  padding: var(--spacing-4xl) 0 var(--spacing-xl) 0 !important;
  margin: 0 !important;
  height: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.guest-honor-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.guest-honor-image:hover {
  transform: translateY(-5px);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
}

.btn-outline-light {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 12px;
  font-weight: 600;
  padding: var(--spacing-md) var(--spacing-xl);
  transition: all 0.3s ease;
}

.btn-outline-light:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: white;
  color: white;
  transform: translateY(-2px);
}


/* Custom Hero Indicators */
.hero-indicators {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 10;
}

.hero-indicators button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
}

.hero-indicators button.active {
  background: #ffffff;
  border-color: #ffffff;
  transform: scale(1.2);
}

.hero-indicators button:hover {
  border-color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.4);
}


/* Large screen optimization */
@media (min-width: 1400px) {
  .hero-full-container {
    padding: 0 100px;
  }

  .hero-full-grid {
    gap: 120px;
  }
}

/* Responsive Design */
@media (max-width: 992px) {
  .hero-full-container {
    padding: 0 40px;
  }

  .hero-full-grid {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }

  .hero-metrics-section {
    justify-content: center;
  }

  .detail-item {
    justify-content: center;
    margin-bottom: var(--spacing-md);
  }

  .hero-actions {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .hero-carousel-section {
    height: 100vh;
    min-height: 600px;
  }

  .hero-full-container {
    padding: 0 20px;
  }

  .hero-full-grid {
    gap: 30px;
  }

  .hero-title {
    font-size: 2.5rem;
    margin-bottom: var(--spacing-md);
    /* Reserve space for two lines to prevent layout shift */
    min-height: calc(2.5rem * 1.2 * 2);
    display: flex;
    align-items: flex-start;
  }


  .hero-subtitle {
    font-size: var(--font-size-base);
    margin-bottom: var(--spacing-lg);
  }

  .hero-actions {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
  }

  .hero-actions .btn {
    margin: 0;
  }

  .guest-honor-image {
    border-radius: 15px;
    border-width: 2px;
  }

}

@media (max-width: 576px) {
  .hero-title {
    font-size: 2rem;
    /* Reserve space for two lines to prevent layout shift */
    min-height: calc(2rem * 1.2 * 2);
    display: flex;
    align-items: flex-start;
  }

  .hero-subtitle {
    font-size: var(--font-size-base);
  }

}

/* Typewriter Animation Styles */
.typewriter-text {
  position: relative;
  display: inline-block;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  background: linear-gradient(135deg, var(--idws-golden-yellow) 0%, var(--idws-vibrant-green) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

/* Fallback for browsers that don't support background-clip: text */
.typewriter-text {
  color: var(--idws-golden-yellow);
}

/* Modern browsers with gradient text support */
@supports (-webkit-background-clip: text) {
  .typewriter-text {
    color: transparent;
  }
}

.typewriter-text::after {
  content: '|';
  font-weight: 100;
  animation: typewriter-blink 1s infinite;
  color: var(--idws-golden-yellow);
  margin-left: 2px;
  background: none !important;
  -webkit-text-fill-color: var(--idws-golden-yellow) !important;
}

.typewriter-text.typing::after {
  animation: none;
  opacity: 1;
}

@keyframes typewriter-blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

/* Mobile typewriter cursor adjustments */
@media (max-width: 576px) {
  .typewriter-text::after {
    font-size: 0.9em;
    margin-left: 1px;
  }
}
</style>